import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Alert, Table, Badge } from 'react-bootstrap';
import axios from 'axios';

const GroupOptionsManagement = ({ restaurantId }) => {
  const [groupOptions, setGroupOptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Форма для группы опций
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    options: [{ name: '', price: 0, isRequired: false, isMultiple: false }]
  });

  const API_BASE = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('restaurantToken') || localStorage.getItem('managerToken');

  // Загрузить группы опций
  const fetchGroupOptions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/group-options/${restaurantId}`, {
        headers: { 'x-auth-token': token }
      });
      setGroupOptions(response.data);
    } catch (error) {
      console.error('Error fetching group options:', error);
      setError('Ошибка при загрузке групп опций');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (restaurantId) {
      fetchGroupOptions();
    }
  }, [restaurantId]);

  // Обработка изменений формы
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Обработка изменений опций
  const handleOptionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    }));
  };

  // Добавить новую опцию
  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { name: '', price: 0, isRequired: false, isMultiple: false }]
    }));
  };

  // Удалить опцию
  const removeOption = (index) => {
    if (formData.options.length > 1) {
      setFormData(prev => ({
        ...prev,
        options: prev.options.filter((_, i) => i !== index)
      }));
    }
  };

  // Открыть модальное окно для создания
  const openCreateModal = () => {
    setFormData({
      name: '',
      description: '',
      options: [{ name: '', price: 0, isRequired: false, isMultiple: false }]
    });
    setEditingGroup(null);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  // Открыть модальное окно для редактирования
  const openEditModal = (group) => {
    setFormData({
      name: group.name,
      description: group.description || '',
      options: group.options.map(opt => ({
        name: opt.name,
        price: opt.price,
        isRequired: opt.isRequired,
        isMultiple: opt.isMultiple
      }))
    });
    setEditingGroup(group);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  // Сохранить группу опций
  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      // Валидация
      if (!formData.name.trim()) {
        setError('Название группы обязательно');
        return;
      }

      const validOptions = formData.options.filter(opt => opt.name.trim());
      if (validOptions.length === 0) {
        setError('Добавьте хотя бы одну опцию');
        return;
      }

      const dataToSave = {
        ...formData,
        options: validOptions
      };

      if (editingGroup) {
        // Обновление
        await axios.put(`/api/group-options/${editingGroup._id}`, dataToSave, {
          headers: { 'x-auth-token': token }
        });
        setSuccess('Группа опций обновлена успешно');
      } else {
        // Создание
        await axios.post(`/api/group-options/${restaurantId}`, dataToSave, {
          headers: { 'x-auth-token': token }
        });
        setSuccess('Группа опций создана успешно');
      }

      setShowModal(false);
      fetchGroupOptions();
    } catch (error) {
      console.error('Error saving group option:', error);
      setError(error.response?.data?.message || 'Ошибка при сохранении группы опций');
    } finally {
      setLoading(false);
    }
  };

  // Удалить группу опций
  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту группу опций?')) {
      try {
        setLoading(true);
        await axios.delete(`/api/group-options/${id}`, {
          headers: { 'x-auth-token': token }
        });
        setSuccess('Группа опций удалена успешно');
        fetchGroupOptions();
      } catch (error) {
        console.error('Error deleting group option:', error);
        setError('Ошибка при удалении группы опций');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4>Группы опций</h4>
              <Button variant="primary" onClick={openCreateModal}>
                Создать группу опций
              </Button>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {success && <Alert variant="success">{success}</Alert>}

              {loading && !groupOptions.length ? (
                <div className="text-center">Загрузка...</div>
              ) : (
                  <div className="row g-3">
                    {groupOptions.map((group) => (
                      <div key={group._id} className="col-md-6 col-lg-4">
                        <Card className="h-100">
                          <Card.Body>
                            <Card.Title className="h6 mb-2">
                              {group.name}
                            </Card.Title>
                            <Card.Text className="text-muted small mb-2">
                              {group.description || 'Без описания'}
                            </Card.Text>
                            <div className="mb-3">
                              <Badge bg="primary" className="me-1">
                                {group.options.length} опций
                              </Badge>
                              {group.options.length > 0 && (
                                <small className="text-muted d-block mt-1">
                                  {group.options.slice(0, 3).map(opt => opt.name).join(', ')}
                                  {group.options.length > 3 && '...'}
                                </small>
                              )}
                            </div>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="flex-fill"
                                onClick={() => openEditModal(group)}
                              >
                                Редактировать
                              </Button>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDelete(group._id)}
                              >
                                Удалить
                              </Button>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    ))}
                  </div>
              )}

              {!loading && groupOptions.length === 0 && (
                <div className="text-center text-muted">
                  <p>Группы опций не найдены</p>
                  <Button variant="primary" onClick={openCreateModal}>
                    Создать первую группу опций
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Модальное окно для создания/редактирования */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingGroup ? 'Редактировать группу опций' : 'Создать группу опций'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Название группы *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Например: Допы к пицце"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Описание</Form.Label>
                  <Form.Control
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Описание группы опций"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Опции группы</h5>
                <Button variant="success" size="sm" onClick={addOption}>
                  <i className="bi bi-plus-circle me-1"></i>
                  Добавить опцию
                </Button>
              </div>

              {formData.options.map((option, index) => (
                <Card key={index} className="mb-3">
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={5}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Название опции *</Form.Label>
                          <Form.Control
                            type="text"
                            value={option.name}
                            onChange={(e) => handleOptionChange(index, 'name', e.target.value)}
                            placeholder="Например: Сыр"
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Цена (BYN)</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={option.price}
                            onChange={(e) => handleOptionChange(index, 'price', parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Обязательная</Form.Label>
                          <div className="mt-2">
                            <Form.Check
                              type="checkbox"
                              checked={option.isRequired}
                              onChange={(e) => handleOptionChange(index, 'isRequired', e.target.checked)}
                              label="Да"
                            />
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label className="fw-semibold">Множественный выбор</Form.Label>
                          <div className="mt-2">
                            <Form.Check
                              type="checkbox"
                              checked={option.isMultiple}
                              onChange={(e) => handleOptionChange(index, 'isMultiple', e.target.checked)}
                              label="Да"
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col className="text-end">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeOption(index)}
                          disabled={formData.options.length === 1}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Удалить опцию
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Отмена
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Сохранение...' : (editingGroup ? 'Обновить' : 'Создать')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default GroupOptionsManagement;
